// 경고 설정 섹션 - 복수 선택 가능한 경고 시스템
import React, { useState, useEffect } from 'react';
import { Form, Checkbox, Row, Col, Input, InputNumber, Select, Card, Space, Typography } from 'antd';
import { FormInstance } from 'antd/es/form';
import { WarningDetail } from '../../types';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface WarningConfigSectionProps {
  form: FormInstance;
}

const WarningConfigSection: React.FC<WarningConfigSectionProps> = ({ form }) => {
  const [warnings, setWarnings] = useState({
    ground: false,
    cast_bar: false,
  });

  const [warningDetail, setWarningDetail] = useState<WarningDetail>({});

  // 폼 값 변화 감지
  const warningValues = Form.useWatch(['warning_ground', 'warning_cast_bar'], form);

  useEffect(() => {
    if (warningValues) {
      setWarnings({
        ground: warningValues[0] || false,
        cast_bar: warningValues[1] || false,
      });
    }
  }, [warningValues]);

  // 경고 상세 설정 업데이트
  const updateWarningDetail = (type: keyof WarningDetail, data: WarningDetail[keyof WarningDetail]) => {
    const newDetail = {
      ...warningDetail,
      [type]: data,
    };
    setWarningDetail(newDetail);
    form.setFieldValue('warning_detail', JSON.stringify(newDetail, null, 2));
  };

  // 미리 정의된 장판 경고 프리셋
  const groundWarningPresets = [
    {
      name: '원형 폭발 (소)',
      value: JSON.stringify({ shape: 'circle', range: 3, color: '#FF0000', fade_in: 0.5 })
    },
    {
      name: '원형 폭발 (중)',
      value: JSON.stringify({ shape: 'circle', range: 5, color: '#FF0000', fade_in: 0.5 })
    },
    {
      name: '원형 폭발 (대)',
      value: JSON.stringify({ shape: 'circle', range: 8, color: '#FF0000', fade_in: 0.5 })
    },
    {
      name: '부채꼴 브레스',
      value: JSON.stringify({ shape: 'sector', range: 10, angle: 120, color: '#FFA500', fade_in: 0.7 })
    },
    {
      name: '직선 돌진',
      value: JSON.stringify({ shape: 'line', range: 15, width: 3, color: '#FF0000', fade_in: 0.3 })
    },
    {
      name: '도넛형 안전지대',
      value: JSON.stringify({ shape: 'donut', range: 8, inner_radius: 3, color: '#0000FF', fade_in: 0.5 })
    },
    {
      name: '사각형 낙하',
      value: JSON.stringify({ shape: 'rectangle', range: 4, color: '#800080', fade_in: 1.0 })
    },
    {
      name: '회복 장판',
      value: JSON.stringify({ shape: 'circle', range: 4, color: '#00FF00', fade_in: 0.3 })
    }
  ];

  // 장판 경고 설정
  const renderGroundWarning = () => {
    return (
      <Card size="small" title="장판 경고 설정" style={{ marginTop: 8 }}>
        <Form.Item name="warning_detail" label="경고 유형">
          <Select
            placeholder="장판 경고 유형을 선택하세요"
            onChange={(value) => {
              if (value === 'custom') {
                // 사용자 정의 선택 시 기본값 설정
                const customDefault = {
                  ground: {
                    shape: 'circle' as const,
                    range: 3,
                    color: '#FF0000',
                    fade_in: 0.5
                  }
                };
                setWarningDetail(customDefault);
                form.setFieldValue('warning_detail', JSON.stringify(customDefault, null, 2));
              } else {
                const parsed = JSON.parse(value);
                setWarningDetail({ ground: parsed });
                form.setFieldValue('warning_detail', JSON.stringify({ ground: parsed }, null, 2));
              }
            }}
          >
            {groundWarningPresets.map(preset => (
              <Option key={preset.name} value={preset.value}>
                {preset.name}
              </Option>
            ))}
            <Option value="custom">사용자 정의</Option>
          </Select>
        </Form.Item>
      </Card>
    );
  };



  // 시전바 경고 설정
  const renderCastBarWarning = () => (
    <Card size="small" title="시전바 설정" style={{ marginTop: 8 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="바 색상">
            <Input
              value={warningDetail.cast_bar?.color || '#FF6600'}
              onChange={(e) => updateWarningDetail('cast_bar', { color: e.target.value, position: warningDetail.cast_bar?.position || 'center' })}
              type="color"
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );


  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text strong>시각적 경고 설정</Text>
      
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="warning_ground" valuePropName="checked">
            <Checkbox>장판 표시</Checkbox>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="warning_cast_bar" valuePropName="checked">
            <Checkbox>시전바</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      {/* 경고 타입별 상세 설정 */}
      {warnings.ground && renderGroundWarning()}
      {warnings.cast_bar && renderCastBarWarning()}

      <Text type="secondary" style={{ fontSize: '12px' }}>
        * 경고 텍스트, 사운드, 대사 등은 트리거 시스템에서 설정합니다.
      </Text>
    </Space>
  );
};

export default WarningConfigSection;